/**
 * Admin Simulator RPC definitions (FM-156)
 *
 * The operator-facing RPC surface for the FlirtMachine Simulator. Two modes plus
 * shared reads, mirroring the admin RPC precedent (see
 * `packages/api/src/rpc/admin/conversations.rpc.ts`): every RPC in the group is
 * gated by {@link AdminMiddleware} — this is an operator surface, so the actor is
 * an admin, not a scoped end user.
 *
 * Manual cockpit (primary):
 *  - `sim.startManual` opens a manual run through the shared TurnEngine seam.
 *  - `sim.sendTurn` runs one persona turn server-side (~15s) and returns that
 *    turn's recorded artifacts. Normal request/response — no streaming in v1.
 *  - `sim.finishRun` fires the Reveal + record-only inference for a manual run.
 *
 * Archetype mode + shared reads:
 *  - `sim.startRuns` fans a batch of archetype runs out onto a bounded-concurrency
 *    daemon and returns the opened run ids immediately.
 *  - `sim.listRuns` lists runs newest-first with server-derived projections.
 *  - `sim.runDetail` returns one run with its joined per-message artifacts.
 *
 * Wire shapes: timestamps cross as `Schema.DateTimeUtc`; response types derive
 * from the `@fm/db` Model.json types and the existing scene/reveal schemas rather
 * than being hand-duplicated.
 *
 * @since 1.0.0
 */
import { Rpc, RpcGroup } from "@effect/rpc";
import { MessageRole } from "@fm/db/models/Message";
import { SimRun, SimRunStatusSchema } from "@fm/db/models/SimRun";
import { Schema } from "effect";
import { AdminMiddleware } from "../admin.tags.ts";
import { RevealBeatSchema, SceneEndingKindSchema } from "../authenticated/conversation.rpc.ts";
import {
  ArchetypeNotFoundError,
  DatabaseError,
  Forbidden,
  NotASceneConversationError,
  SceneEndedError,
  SceneNotFoundError,
  SimRunNotFoundError,
  Unauthorized,
} from "../errors.ts";

// ============================================================================
// Request Schemas
// ============================================================================

/**
 * `sim.startManual` payload — the scene to open a manual run against.
 * @since 1.0.0
 */
export const SimStartManualInput = Schema.Struct({
  sceneKey: Schema.String,
});
export type SimStartManualInput = Schema.Schema.Type<typeof SimStartManualInput>;

/**
 * `sim.sendTurn` payload — the run to advance and the operator's line.
 * @since 1.0.0
 */
export const SimSendTurnInput = Schema.Struct({
  runId: Schema.String,
  text: Schema.String,
});
export type SimSendTurnInput = Schema.Schema.Type<typeof SimSendTurnInput>;

/**
 * `sim.finishRun` payload — the manual run to reveal + close.
 * @since 1.0.0
 */
export const SimFinishRunInput = Schema.Struct({
  runId: Schema.String,
});
export type SimFinishRunInput = Schema.Schema.Type<typeof SimFinishRunInput>;

/**
 * `sim.startRuns` payload — the archetype keys to drive against a scene and how
 * many runs to open per archetype. Keys are validated against the archetype and
 * scene registries before any run is opened.
 * @since 1.0.0
 */
export const SimStartRunsInput = Schema.Struct({
  archetypeKeys: Schema.Array(Schema.String),
  sceneKey: Schema.String,
  countPerArchetype: Schema.Int,
});
export type SimStartRunsInput = Schema.Schema.Type<typeof SimStartRunsInput>;

/**
 * `sim.runDetail` payload — the run to expand.
 * @since 1.0.0
 */
export const SimRunDetailInput = Schema.Struct({
  runId: Schema.String,
});
export type SimRunDetailInput = Schema.Schema.Type<typeof SimRunDetailInput>;

// ============================================================================
// Response Schemas
// ============================================================================

/**
 * `sim.startManual` result — the opened run, its scene conversation, and the
 * scene's opening assistant line the cockpit renders first.
 * @since 1.0.0
 */
export const SimStartManualResult = Schema.Struct({
  runId: Schema.String,
  conversationId: Schema.String,
  openingMessage: Schema.String,
});
export type SimStartManualResult = Schema.Schema.Type<typeof SimStartManualResult>;

/**
 * A turn's assessment reduced to the four fields the cockpit surfaces: the
 * leading move and its graded intensity, plus what the moment invited and
 * whether it was met. Mirrors `TurnEngine.TurnAssessment`.
 * @since 1.0.0
 */
export const SimTurnAssessment = Schema.Struct({
  move: Schema.String,
  intensity: Schema.String,
  invited: Schema.String,
  met: Schema.Literal("yes", "partial", "no"),
});
export type SimTurnAssessment = Schema.Schema.Type<typeof SimTurnAssessment>;

/**
 * One recorded trigger ruling for a turn.
 * @since 1.0.0
 */
export const SimTurnTriggerEvent = Schema.Struct({
  triggerKey: Schema.String,
  activated: Schema.Boolean,
  why: Schema.NullOr(Schema.String),
});
export type SimTurnTriggerEvent = Schema.Schema.Type<typeof SimTurnTriggerEvent>;

/**
 * The scene's running status after a turn.
 * @since 1.0.0
 */
export const SimTurnSceneStatus = Schema.Struct({
  messagesUsed: Schema.Number,
  budget: Schema.Number,
  ended: Schema.Boolean,
  endingKind: Schema.optional(SceneEndingKindSchema),
});
export type SimTurnSceneStatus = Schema.Schema.Type<typeof SimTurnSceneStatus>;

/**
 * `sim.sendTurn` result — the full artifacts of one persona turn: the reply, the
 * turn's assessment (or `null` when degraded), the per-trigger rulings, and the
 * resulting scene status.
 * @since 1.0.0
 */
export const SimSendTurnResult = Schema.Struct({
  assistantText: Schema.String,
  assessment: Schema.NullOr(SimTurnAssessment),
  triggerEvents: Schema.Array(SimTurnTriggerEvent),
  sceneStatus: SimTurnSceneStatus,
});
export type SimSendTurnResult = Schema.Schema.Type<typeof SimSendTurnResult>;

/**
 * `sim.finishRun` result — the generated (or re-read) Reveal beats and the
 * record-only psyche inference stored on the run.
 * @since 1.0.0
 */
export const SimFinishRunResult = Schema.Struct({
  revealBeats: Schema.Array(RevealBeatSchema),
  inference: Schema.String,
});
export type SimFinishRunResult = Schema.Schema.Type<typeof SimFinishRunResult>;

/**
 * `sim.startRuns` result — the ids of the runs opened by the batch. The runs
 * progress server-side on a bounded-concurrency daemon after the RPC returns.
 * @since 1.0.0
 */
export const SimStartRunsResult = Schema.Struct({
  runIds: Schema.Array(Schema.String),
});
export type SimStartRunsResult = Schema.Schema.Type<typeof SimStartRunsResult>;

/**
 * One row of the `sim.listRuns` projection: the run's identity plus the
 * server-derived archetype display name, ending kind (from the scene
 * conversation's messages when the run completed), and the `pass/total`
 * expectation score (null for manual runs, which carry no expectations).
 * @since 1.0.0
 */
export const SimRunListItem = Schema.Struct({
  id: Schema.String,
  archetypeKey: Schema.NullOr(Schema.String),
  archetypeName: Schema.NullOr(Schema.String),
  sceneKey: Schema.String,
  status: SimRunStatusSchema,
  endingKind: Schema.NullOr(SceneEndingKindSchema),
  expectationScore: Schema.NullOr(Schema.String),
  createdAt: Schema.DateTimeUtc,
});
export type SimRunListItem = Schema.Schema.Type<typeof SimRunListItem>;

/**
 * A run-detail message's assessment reduced to the four fields the detail view
 * renders: the leading move and its graded intensity word, plus the invited/met
 * fit. `null` when the turn recorded no assessment (degraded).
 * @since 1.0.0
 */
export const SimMessageAssessment = Schema.Struct({
  move: Schema.String,
  intensityWord: Schema.String,
  invited: Schema.String,
  met: Schema.Literal("yes", "partial", "no"),
});
export type SimMessageAssessment = Schema.Schema.Type<typeof SimMessageAssessment>;

/**
 * A run-detail message's trigger ruling reduced to its key and activation.
 * @since 1.0.0
 */
export const SimMessageTriggerEvent = Schema.Struct({
  triggerKey: Schema.String,
  activated: Schema.Boolean,
});
export type SimMessageTriggerEvent = Schema.Schema.Type<typeof SimMessageTriggerEvent>;

/**
 * One message in the run-detail transcript, joined with its recorded assessment
 * and trigger events.
 * @since 1.0.0
 */
export const SimRunDetailMessage = Schema.Struct({
  role: MessageRole,
  text: Schema.String,
  assessment: Schema.NullOr(SimMessageAssessment),
  triggerEvents: Schema.Array(SimMessageTriggerEvent),
});
export type SimRunDetailMessage = Schema.Schema.Type<typeof SimRunDetailMessage>;

/**
 * `sim.runDetail` result — the full run row (incl. expectation results,
 * inference, error) derived from the `SimRun` model, the joined transcript, the
 * Reveal beats (each carrying its grounding quote), and the degraded-turn count.
 * @since 1.0.0
 */
export const SimRunDetailResponse = Schema.Struct({
  run: SimRun.json,
  messages: Schema.Array(SimRunDetailMessage),
  revealBeats: Schema.Array(RevealBeatSchema),
  degradedTurns: Schema.Number,
});
export type SimRunDetailResponse = Schema.Schema.Type<typeof SimRunDetailResponse>;

// ============================================================================
// RPC Group
// ============================================================================

/**
 * Admin Simulator RPC group.
 *
 * Every endpoint requires admin role via {@link AdminMiddleware}. This is an
 * operator surface — actor-scoped middleware would be wrong here.
 *
 * @since 1.0.0
 */
export class SimRpcs extends RpcGroup.make(
  Rpc.make("sim.startManual", {
    payload: SimStartManualInput,
    success: SimStartManualResult,
    error: Schema.Union(Unauthorized, Forbidden, SceneNotFoundError, DatabaseError),
  }).middleware(AdminMiddleware),
  Rpc.make("sim.sendTurn", {
    payload: SimSendTurnInput,
    success: SimSendTurnResult,
    error: Schema.Union(
      Unauthorized,
      Forbidden,
      SimRunNotFoundError,
      NotASceneConversationError,
      SceneEndedError,
      DatabaseError,
    ),
  }).middleware(AdminMiddleware),
  Rpc.make("sim.finishRun", {
    payload: SimFinishRunInput,
    success: SimFinishRunResult,
    error: Schema.Union(Unauthorized, Forbidden, SimRunNotFoundError, SceneNotFoundError, DatabaseError),
  }).middleware(AdminMiddleware),
  Rpc.make("sim.startRuns", {
    payload: SimStartRunsInput,
    success: SimStartRunsResult,
    error: Schema.Union(Unauthorized, Forbidden, SceneNotFoundError, ArchetypeNotFoundError, DatabaseError),
  }).middleware(AdminMiddleware),
  Rpc.make("sim.listRuns", {
    payload: Schema.Struct({}),
    success: Schema.Array(SimRunListItem),
    error: Schema.Union(Unauthorized, Forbidden, DatabaseError),
  }).middleware(AdminMiddleware),
  Rpc.make("sim.runDetail", {
    payload: SimRunDetailInput,
    success: SimRunDetailResponse,
    error: Schema.Union(Unauthorized, Forbidden, SimRunNotFoundError, DatabaseError),
  }).middleware(AdminMiddleware),
) {}
